<template>
  <div class="-mx-4 -mt-8 pb-16 sm:-mx-0 sm:mt-0 sm:pb-8">
    <!-- Hero -->
    <section
      class="relative overflow-hidden rounded-b-[2rem] border-b border-primary-100/80 bg-gradient-to-b from-primary-50/90 via-white to-gray-50/50 px-5 pb-20 pt-12 sm:px-10 sm:pb-24 sm:pt-16"
    >
      <div
        class="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-primary-200/35 blur-3xl"
      />
      <div
        class="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-sky-200/25 blur-3xl"
      />
      <div class="relative mx-auto max-w-2xl text-center">
        <p
          class="inline-block rounded-full border border-primary-200/60 bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary-800 shadow-sm backdrop-blur-sm"
        >
          {{ t('landing.hero.brand') }}
        </p>
        <h1 class="mt-8 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
          <span class="bg-gradient-to-r from-primary-600 via-sky-500 to-primary-500 bg-clip-text text-transparent">
            {{ t('landing.hero.name') }}
          </span>
        </h1>
        <p
          class="mx-auto mt-8 max-w-xl text-base leading-relaxed text-gray-600 sm:text-lg md:text-xl"
        >
          {{ t('landing.hero.tagline') }}
        </p>
        <ClientOnly>
          <div class="mt-12 flex flex-wrap items-center justify-center gap-3">
            <UButton
              v-if="authStore.isAuthenticated"
              size="lg"
              color="primary"
              :to="localePath(authStore.dashboardPath)"
            >
              {{ t(authStore.dashboardLabelKey) }}
            </UButton>
            <UButton
              size="lg"
              variant="ghost"
              color="gray"
              :to="localePath('/contact')"
              class="text-gray-700 hover:bg-primary-50 hover:text-primary-800"
            >
              {{ t('footer.contact') }}
            </UButton>
          </div>
        </ClientOnly>
      </div>
    </section>

    <!-- Value proposition -->
    <section class="mx-auto -mt-8 max-w-3xl px-4 sm:px-0">
      <div
        class="rounded-2xl border border-gray-100 bg-white p-8 shadow-md shadow-gray-200/40 ring-1 ring-gray-100 sm:p-10"
      >
        <p class="text-center text-lg leading-relaxed text-gray-700 sm:text-xl">
          {{ t('landing.teaser.marketplace') }}
        </p>
      </div>
    </section>

    <!-- Mobile apps -->
    <section class="mx-auto mt-16 max-w-4xl px-4 sm:px-0">
      <div class="mb-8 text-center">
        <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {{ t('landing.apps.title') }}
        </h2>
        <div class="mx-auto mt-3 h-1 w-12 rounded-full bg-gradient-to-r from-primary-500 to-sky-400" />
      </div>
      <div class="grid gap-10 sm:grid-cols-2 sm:gap-8">
        <div
          class="flex flex-col items-center rounded-2xl border border-gray-100 bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8"
        >
          <img
            src="/images/iphone.png"
            :alt="t('landing.apps.ios_qr_alt')"
            class="mx-auto h-auto max-h-48 w-auto max-w-full object-contain"
            width="200"
            height="200"
          >
          <a
            :href="appLinks.ios"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-6 inline-block transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <img
              src="/images/appstoremdpi.png"
              :alt="t('landing.apps.app_store_alt')"
              class="h-10 w-auto sm:h-11"
              width="120"
              height="40"
            >
          </a>
        </div>
        <div
          class="flex flex-col items-center rounded-2xl border border-gray-100 bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8"
        >
          <img
            src="/images/android.png"
            :alt="t('landing.apps.android_qr_alt')"
            class="mx-auto h-auto max-h-48 w-auto max-w-full object-contain"
            width="200"
            height="200"
          >
          <a
            :href="appLinks.android"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-6 inline-block transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          >
            <img
              src="/images/google-play-badge.png"
              :alt="t('landing.apps.google_play_alt')"
              class="h-12 w-auto sm:h-14"
              width="155"
              height="60"
            >
          </a>
        </div>
      </div>
    </section>

    <!-- Explore -->
    <section class="mx-auto mt-16 max-w-4xl px-4 sm:px-0">
      <div class="mb-8 text-center">
        <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {{ t('landing.explore.title') }}
        </h2>
        <div class="mx-auto mt-3 h-1 w-12 rounded-full bg-gradient-to-r from-primary-500 to-sky-400" />
      </div>
      <div class="grid gap-5 sm:grid-cols-2">
        <NuxtLink
          v-for="card in exploreCards"
          :key="card.to"
          :to="localePath(card.to)"
          class="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-primary-200/80 hover:shadow-lg hover:shadow-primary-500/5 sm:p-7"
        >
          <div
            class="absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-primary-50/80 opacity-0 transition-opacity group-hover:opacity-100"
          />
          <div class="relative flex items-start gap-4">
            <span
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-primary-100 transition group-hover:bg-primary-100 group-hover:ring-primary-200"
            >
              <UIcon :name="card.icon" class="h-6 w-6" />
            </span>
            <div class="min-w-0 flex-1 text-left">
              <h3 class="font-semibold text-gray-900 transition group-hover:text-primary-700">
                {{ card.title }}
              </h3>
              <p class="mt-2 text-sm leading-relaxed text-gray-600">
                {{ card.desc }}
              </p>
            </div>
            <UIcon
              name="i-heroicons-arrow-right-20-solid"
              class="mt-1 h-5 w-5 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-500"
            />
          </div>
        </NuxtLink>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '~/stores/auth'

const { t } = useI18n()
const localePath = useLocalePath()
const authStore = useAuthStore()

const appLinks = {
  ios: 'https://apps.apple.com/jp/app/rideblink-bike-sharing/id6444598723?l=jp',
  android: 'https://play.google.com/store/apps/details?id=com.rideblink.blinkAndroid',
} as const

useHead(() => ({
  title: t('landing.meta_title'),
  meta: [{ name: 'description', content: t('landing.meta_description') }],
}))

const exploreCards = computed(() => [
  { to: '/about', title: t('landing.explore.about'), desc: t('landing.explore.about_desc'), icon: 'i-heroicons-building-office-2' },
  { to: '/contact', title: t('landing.explore.contact'), desc: t('landing.explore.contact_desc'), icon: 'i-heroicons-envelope' },
  { to: '/locations', title: t('landing.explore.locations'), desc: t('landing.explore.locations_desc'), icon: 'i-heroicons-map-pin' },
  { to: '/pricing', title: t('landing.explore.pricing'), desc: t('landing.explore.pricing_desc'), icon: 'i-heroicons-banknotes' },
  { to: '/how-to-lend', title: t('landing.explore.how_to_lend'), desc: t('landing.explore.how_to_lend_desc'), icon: 'i-heroicons-clipboard-document-list' },
  { to: '/how-to-use-app', title: t('landing.explore.how_to_app'), desc: t('landing.explore.how_to_app_desc'), icon: 'i-heroicons-device-phone-mobile' },
  { to: '/install-lock', title: t('landing.explore.install_lock'), desc: t('landing.explore.install_lock_desc'), icon: 'i-heroicons-lock-closed' },
  { to: '/troubleshooting', title: t('landing.explore.troubleshooting'), desc: t('landing.explore.troubleshooting_desc'), icon: 'i-heroicons-lifebuoy' },
])
</script>
